/**
 * Cart Visibility Manager
 * Handles visibility of cart upsell card and cart reviews based on cart value and upsell product presence
 */
class CartVisibilityManager {
  constructor() {
    this.upsellProductId = null;
    this.isCheckingVisibility = false; // Prevent multiple simultaneous checks
    this.visibilityCheckTimeout = null; // For debouncing
    this.isCartUpdating = false; // Track if cart is currently updating
    this.cartUpdateTimeout = null; // Timeout to reset cart updating state
    this.minimumItemsForUpsell = 1;
    this.isApplyingDiscount = false;
    this.pendingDiscountCode = null;
    this.upsellDiscountCode = null;
    this.init();
  }

  init() {
    this.bindEvents();
    this.setupUpsellProductId();
    this.setupMutationObserver();
    // Delay initial visibility check to ensure DOM and cart are ready
    setTimeout(() => this.checkVisibility(), 500);
    // Apply promotional item styles on page load
    setTimeout(() => this.hidePromotionalItemQuantitySelectors(), 1000);
  }

  bindEvents() {
    // Handle variant selection for upsell card
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-upsell-variant-select]')) {
        this.handleVariantChange(e.target);
      }
    });

    // Listen for cart refresh events from the theme
    document.addEventListener('cart:refresh', (e) => {
      if (e.detail && e.detail.source !== 'visibility-manager') {
        this.refreshCart();
        // Don't call checkVisibility here - let the refresh complete first
      }
    });

    // Listen for cart content replacement events (when API cart items are updated)
    document.addEventListener('cart:content:replaced', (e) => {
      console.log('[Cart Upsell] cart:content:replaced event received', e.detail);
      // Small delay to ensure the new content is fully rendered
      setTimeout(() => {
        console.log('[Cart Upsell] Running visibility check after content replacement');
        this.checkVisibility();
      }, 200);
    });

    // Listen for cart drawer open events
    document.addEventListener('cart:open', () => {
      this.refreshCart();
      // Delay visibility check to let cart data load
      setTimeout(() => this.checkVisibility(), 300);
    });

    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      // Mark cart update as completed and check visibility
      this.setCartUpdating(false);
      // Hide quantity selectors and show remove buttons for promotional items
      this.hidePromotionalItemQuantitySelectors();
      this.checkVisibility();
    });

    // Listen for cart item removal
    document.addEventListener('cart:item:removed', () => {
      // Mark cart update as completed and check visibility
      this.setCartUpdating(false);
      // Hide quantity selectors and show remove buttons for promotional items
      this.hidePromotionalItemQuantitySelectors();
      this.checkVisibility();
    });

    // Listen for cart changes (general cart modifications)
    document.addEventListener('cart:change', () => {
      // Mark cart update as completed and check visibility
      this.setCartUpdating(false);
      // Hide quantity selectors and show remove buttons for promotional items
      this.hidePromotionalItemQuantitySelectors();
      this.checkVisibility();
    });

    // Listen for theme cart update events
    document.addEventListener('theme:cart:update', () => {
      // Mark cart update as started
      this.setCartUpdating(true);
    });

    // Listen for quantity changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-cart-quantity]')) {
        // Mark cart update as started
        this.setCartUpdating(true);
      }
    });

    // Listen for add to cart form submissions
    document.addEventListener('submit', (e) => {
      if (e.target.matches('form[action*="/cart/add"]')) {
        // Check if this is an upsell form
        const upsellCard = e.target.closest('[data-cart-upsell]');
        if (upsellCard) {
          console.log('[Cart Upsell] Upsell form submission detected');
          // Set loading state for upsell forms
          this.setFormLoadingState(e.target, true);
          // Mark cart update as started for upsell forms
          this.setCartUpdating(true);
          console.log('[Cart Upsell] Marked cart as updating');
        } else {
          // For non-upsell forms, just check visibility normally
          this.debouncedCheckVisibility();
        }
      }
    });

    document.addEventListener('theme:product:add', (event) => {
      this.handleProductAdded(event);
    });

    // Handle add to cart button clicks for dynamic updates
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-add-to-cart]')) {
        const upsellCard = e.target.closest('[data-cart-upsell]');
        if (upsellCard) {
          // For upsell cards, the form submission will handle visibility
          return;
        }
        // For other add to cart buttons, check visibility after a delay
        this.debouncedCheckVisibility();
      }
    });

    // Handle button focus states for accessibility
    document.addEventListener('focusin', (e) => {
      if (e.target.matches('[data-add-to-cart]')) {
        e.target.style.outline = '2px solid #6fb576';
        e.target.style.outlineOffset = '2px';
      }
    });

    document.addEventListener('focusout', (e) => {
      if (e.target.matches('[data-add-to-cart]')) {
        e.target.style.outline = '';
        e.target.style.outlineOffset = '';
      }
    });
  }

  /**
   * Debounced visibility check to prevent multiple rapid calls
   */
  debouncedCheckVisibility() {
    if (this.visibilityCheckTimeout) {
      clearTimeout(this.visibilityCheckTimeout);
    }
    this.visibilityCheckTimeout = setTimeout(() => {
      this.checkVisibility();
    }, 150);
  }

  /**
   * Set cart updating state with automatic timeout reset
   */
  setCartUpdating(isUpdating) {
    // Clear any existing timeout
    if (this.cartUpdateTimeout) {
      clearTimeout(this.cartUpdateTimeout);
      this.cartUpdateTimeout = null;
    }

    this.isCartUpdating = isUpdating;
    
    if (isUpdating) {
      // Set a timeout to automatically reset the cart updating state
      // This prevents the cart from being stuck in updating state forever
      this.cartUpdateTimeout = setTimeout(() => {
        this.isCartUpdating = false;
        // Now check visibility since cart should be stable
        this.checkVisibility();
      }, 3000); // 3 second timeout
    }
  }

  /**
   * Hide quantity selectors and show remove buttons for promotional items with $.99 selling plan
   */
  hidePromotionalItemQuantitySelectors() {
    // Find all cart items
    const cartItems = document.querySelectorAll('.cart__item');
    
    cartItems.forEach(item => {
      // Check if this item has the promotional selling plan text
      const sellingPlanElement = item.querySelector('.cart__item__selling-plan span');
      
      if (sellingPlanElement && sellingPlanElement.textContent.includes('Try Electrolytes for $.99 ONLY')) {
        // Add promotional item attribute for CSS targeting
        item.setAttribute('data-promotional-item', 'true');
        item.classList.add('cart__item--promotional');
        
        // Find the quantity selector for this item
        const quantitySelector = item.querySelector('.cart__quantity-counter');
        
        if (quantitySelector) {
          // Hide the quantity selector
          quantitySelector.style.display = 'none';
        }
        
        // Find and show the remove button for this item
        const removeButton = item.querySelector('.cart__item__remove');
        if (removeButton) {
          removeButton.classList.remove('hidden');
          removeButton.style.display = 'block';
          removeButton.style.opacity = '1';
          removeButton.style.visibility = 'visible';
        }
      }
    });
  }

  /**
   * Show loading state for cart elements during updates
   */
  showLoadingState() {
    // Show loading for cart upsell card
    const upsellCards = document.querySelectorAll('[data-cart-upsell]');
    upsellCards.forEach(card => {
      if (!card.querySelector('.cart-loading-indicator')) {
        const loadingIndicator = document.createElement('div');
        loadingIndicator.className = 'cart-loading-indicator';
        loadingIndicator.innerHTML = `
          <div class="loading-spinner">
            <div class="spinner"></div>
            <span>Updating cart...</span>
          </div>
        `;
        card.appendChild(loadingIndicator);
      }
    });

    // Show loading for cart reviews
    const cartReviews = document.querySelectorAll('.CP_TE_Sitewide_07_V2');
    cartReviews.forEach(review => {
      if (!review.querySelector('.cart-loading-indicator')) {
        const loadingIndicator = document.createElement('div');
        loadingIndicator.className = 'cart-loading-indicator';
        loadingIndicator.innerHTML = `
          <div class="loading-spinner">
            <div class="spinner"></div>
            <span>Updating cart...</span>
          </div>
        `;
        review.appendChild(loadingIndicator);
      }
    });
  }

  /**
   * Hide loading state for cart elements
   */
  hideLoadingState() {
    // Hide loading for cart upsell card
    const upsellCards = document.querySelectorAll('[data-cart-upsell]');
    upsellCards.forEach(card => {
      const loadingIndicator = card.querySelector('.cart-loading-indicator');
      if (loadingIndicator) {
        loadingIndicator.remove();
      }
    });

    // Hide loading for cart reviews
    const cartReviews = document.querySelectorAll('.CP_TE_Sitewide_07_V2');
    cartReviews.forEach(review => {
      const loadingIndicator = review.querySelector('.cart-loading-indicator');
      if (loadingIndicator) {
        loadingIndicator.remove();
      }
    });
  }

  /**
   * Setup mutation observer to detect when cart content is re-rendered
   */
  setupMutationObserver() {
    // Prevent duplicate observers
    if (this.observers && this.observers.length > 0) {
      return;
    }

    // Look for cart content containers that might be re-rendered
    const cartContainers = document.querySelectorAll('[data-api-content], [data-cart-drawer], [data-cart-page]');
    
    if (cartContainers.length === 0) {
      return;
    }
    
    this.observers = [];
    
    cartContainers.forEach((container, index) => {
      const observer = new MutationObserver((mutations) => {
        let shouldCheckVisibility = false;
        let upsellCardAdded = false;
        let cartContentReplaced = false;
        
        mutations.forEach((mutation) => {
          // Check if cart upsell card was added or modified
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                // Check if the added node is a cart upsell card or contains one
                if (node.matches('[data-cart-upsell]') || node.querySelector('[data-cart-upsell]')) {
                  upsellCardAdded = true;
                  shouldCheckVisibility = true;
                }
                // Check if cart content was re-rendered
                if (node.matches('[data-api-content]') || node.querySelector('[data-api-content]')) {
                  cartContentReplaced = true;
                  shouldCheckVisibility = true;
                }
              }
            });
          }
        });
        
        if (shouldCheckVisibility) {
          // Small delay to ensure the new content is fully rendered
          setTimeout(() => {
            this.restoreVisibilityState();
            // Apply promotional item styles
            this.applyPromotionalItemStyles();
            // Only check visibility if cart is not updating
            if (!this.isCartUpdating) {
              this.checkVisibility();
            }
          }, 100);
        }
      });
      
      // Start observing
      observer.observe(container, {
        childList: true,
        subtree: true
      });
      
      // Store observer reference for cleanup
      this.observers.push(observer);
    });
  }

  /**
   * Restore visibility state after cart content re-rendering
   */
  restoreVisibilityState() {
    const upsellCards = document.querySelectorAll('[data-cart-upsell]');
    
    upsellCards.forEach(card => {
      const shouldBeVisible = card.dataset.shouldBeVisible === 'true';
      if (shouldBeVisible && card.classList.contains('hidden')) {
        card.classList.remove('hidden');
      } else if (!shouldBeVisible && !card.classList.contains('hidden')) {
        card.classList.add('hidden');
      }
    });

    // Hide quantity selectors and show remove buttons for promotional items
    this.hidePromotionalItemQuantitySelectors();

    // After restoring state, schedule a final visibility check to ensure consistency
    setTimeout(() => {
      // Only check visibility if cart is not updating
      if (!this.isCartUpdating) {
        this.checkVisibility();
      }
    }, 500);
  }

  /**
   * Apply promotional item styles to all cart items
   */
  applyPromotionalItemStyles() {
    // Apply styles to existing items
    this.hidePromotionalItemQuantitySelectors();
    
    // Also check for any new items that might have been added
    setTimeout(() => {
      this.hidePromotionalItemQuantitySelectors();
    }, 100);
  }

  setupUpsellProductId() {
    // Get the upsell product ID from the cart upsell card
    const upsellCard = document.querySelector('[data-cart-upsell]');
    if (upsellCard) {
      this.upsellProductId = upsellCard.dataset.productId;
      this.upsellDiscountCode = upsellCard.dataset.discountCode || null;
    }
    
    // Setup variant selector functionality
    this.setupVariantSelector();
    this.initializeUpsellPricing();
  }

  setupVariantSelector() {
    const variantSelects = document.querySelectorAll('[data-upsell-variant-select]');
    
    variantSelects.forEach(select => {
      // Set initial variant image
      const selectedOption = select.options[select.selectedIndex];
      const upsellCard = select.closest('[data-cart-upsell]');
      if (selectedOption && selectedOption.dataset.variantImage) {
        this.updateProductImage(upsellCard, selectedOption.dataset.variantImage);
      }
      if (selectedOption && selectedOption.dataset.variantPrice) {
        const variantPrice = parseInt(selectedOption.dataset.variantPrice, 10);
        if (!Number.isNaN(variantPrice)) {
          this.updatePricingDisplay(upsellCard, variantPrice);
        }
      }
    });
  }

  initializeUpsellPricing() {
    const upsellCards = document.querySelectorAll('[data-cart-upsell]');

    upsellCards.forEach(card => {
      const initialPrice = parseInt(card.dataset.initialVariantPrice, 10);
      if (!Number.isNaN(initialPrice)) {
        this.updatePricingDisplay(card, initialPrice);
      }
    });
  }

  updatePricingDisplay(upsellCard, variantPrice) {
    if (!upsellCard || Number.isNaN(variantPrice)) {
      return;
    }

    const discountPercentage = parseInt(upsellCard.dataset.discountPercentage, 10);
    const discount = Number.isNaN(discountPercentage) ? 25 : discountPercentage;
    const discountMultiplier = 100 - discount;
    const discountedPrice = Math.round((variantPrice * discountMultiplier) / 100);
    const savings = variantPrice - discountedPrice;

    const originalPriceEl = upsellCard.querySelector('[data-upsell-original-price]');
    if (originalPriceEl) {
      originalPriceEl.textContent = this.formatMoney(variantPrice);
    }

    const discountedPriceEl = upsellCard.querySelector('[data-upsell-discounted-price]');
    if (discountedPriceEl) {
      discountedPriceEl.textContent = this.formatMoney(discountedPrice);
    }

    const savingsBadge = upsellCard.querySelector('[data-upsell-savings-badge]');
    if (savingsBadge) {
      savingsBadge.textContent = `YOU SAVE ${this.formatMoney(savings)}`;
    }
  }

  formatMoney(cents) {
    const amount = parseInt(cents, 10);

    if (typeof themeCurrency !== 'undefined' && typeof themeCurrency.formatMoney === 'function' && typeof theme !== 'undefined') {
      return themeCurrency.formatMoney(amount, theme.moneyFormat);
    }

    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      const moneyFormat = (typeof theme !== 'undefined' && theme.moneyFormat) ? theme.moneyFormat : '{{amount}}';
      return Shopify.formatMoney(amount, moneyFormat);
    }

    const fallbackAmount = ((Number.isNaN(amount) ? 0 : amount) / 100).toFixed(2);
    return `$${fallbackAmount}`;
  }

  handleProductAdded(event) {
    console.log('[Cart Upsell] handleProductAdded called', event);
    const button = event?.detail?.button;
    if (!button) {
      console.log('[Cart Upsell] No button found in event');
      return;
    }

    const upsellCard = button.closest('[data-cart-upsell]');
    if (!upsellCard) {
      console.log('[Cart Upsell] Button is not from an upsell card');
      return;
    }

    console.log('[Cart Upsell] Upsell product added detected');
    const discountCode = upsellCard.dataset.discountCode || this.upsellDiscountCode;
    if (!discountCode) {
      console.log('[Cart Upsell] No discount code found');
      return;
    }

    console.log('[Cart Upsell] Applying discount code:', discountCode);
    this.ensureDiscountApplied(discountCode);
  }

  async ensureDiscountApplied(discountCode) {
    console.log('[Cart Upsell] ensureDiscountApplied called with code:', discountCode);
    if (!discountCode) {
      console.log('[Cart Upsell] No discount code provided');
      return;
    }

    if (this.isApplyingDiscount) {
      console.log('[Cart Upsell] Discount already being applied, queuing:', discountCode);
      this.pendingDiscountCode = discountCode;
      return;
    }

    console.log('[Cart Upsell] Starting discount application process');
    this.isApplyingDiscount = true;

    try {
      console.log('[Cart Upsell] Step 1: Fetching current cart data...');
      const cartData = await this.fetchCartData();
      console.log('[Cart Upsell] Current cart data:', cartData);
      
      if (this.cartHasDiscount(cartData, discountCode)) {
        console.log('[Cart Upsell] Discount already applied in cart, refreshing to show prices');
        // Still refresh to ensure prices are displayed correctly
        this.refreshCart();
        return;
      }

      console.log('[Cart Upsell] Step 2: Updating cart item prices immediately in UI...');
      // First, immediately update prices in the cart drawer for better UX
      this.updateCartItemPricesImmediately(discountCode);

      console.log('[Cart Upsell] Step 3: Applying discount code via Cart API...');
      // Apply discount via Cart API
      const updatedCartData = await this.applyDiscountCode(discountCode);
      console.log('[Cart Upsell] Discount applied, updated cart data:', updatedCartData);

      console.log('[Cart Upsell] Step 4: Waiting for discount to be fully processed on server...');
      // Wait longer for the discount to be fully processed on the server
      // This ensures the server-side cart object has the discount when we fetch the HTML
      await new Promise(resolve => setTimeout(resolve, 500));

      console.log('[Cart Upsell] Step 5: Verifying discount was applied...');
      // Verify discount was applied - try multiple times if needed
      let verificationData = updatedCartData || await this.fetchCartData();
      let hasDiscount = this.cartHasDiscount(verificationData, discountCode);
      let retryCount = 0;
      const maxRetries = 3;

      // Retry verification if discount not found
      while (!hasDiscount && retryCount < maxRetries) {
        console.log(`[Cart Upsell] Discount not found, retrying verification (${retryCount + 1}/${maxRetries})...`);
        await new Promise(resolve => setTimeout(resolve, 300));
        verificationData = await this.fetchCartData();
        hasDiscount = this.cartHasDiscount(verificationData, discountCode);
        retryCount++;
      }

      console.log('[Cart Upsell] Discount verification result:', hasDiscount, verificationData);

      if (hasDiscount) {
        console.log('[Cart Upsell] Step 6: Discount confirmed, waiting a bit more for server to process...');
        // Wait a bit more to ensure server-side cart HTML will have the discount
        await new Promise(resolve => setTimeout(resolve, 300));
        console.log('[Cart Upsell] Refreshing cart now...');
        // Refresh cart after discount is confirmed
        this.refreshCart();
      } else {
        console.log('[Cart Upsell] Discount not found after retries, refreshing anyway...');
        // Refresh anyway - the discount might still be applied
        setTimeout(() => {
          console.log('[Cart Upsell] Retrying cart refresh...');
          this.refreshCart();
        }, 500);
      }
    } catch (error) {
      console.error('[Cart Upsell] Error applying discount:', error);
      // Still try to refresh cart
      console.log('[Cart Upsell] Refreshing cart despite error...');
      this.refreshCart();
    } finally {
      this.isApplyingDiscount = false;
      console.log('[Cart Upsell] Discount application process completed');

      if (this.pendingDiscountCode) {
        const nextCode = this.pendingDiscountCode;
        this.pendingDiscountCode = null;
        if (nextCode.toLowerCase() !== discountCode.toLowerCase()) {
          console.log('[Cart Upsell] Processing pending discount code:', nextCode);
          this.ensureDiscountApplied(nextCode);
        }
      }
    }
  }

  /**
   * Immediately update cart item prices in the UI before server confirms discount
   * This provides instant feedback while the discount is being applied
   */
  updateCartItemPricesImmediately(discountCode) {
    console.log('[Cart Upsell] updateCartItemPricesImmediately called');
    const upsellCard = document.querySelector('[data-cart-upsell]');
    if (!upsellCard) {
      console.log('[Cart Upsell] No upsell card found for immediate price update');
      return;
    }

    const discountPercentage = parseInt(upsellCard.dataset.discountPercentage, 10);
    const upsellProductId = upsellCard.dataset.productId;
    if (Number.isNaN(discountPercentage) || !upsellProductId) {
      console.log('[Cart Upsell] Invalid discount percentage or product ID', { discountPercentage, upsellProductId });
      return;
    }

    console.log('[Cart Upsell] Calculating discount:', { discountPercentage, upsellProductId });
    const discountMultiplier = (100 - discountPercentage) / 100;

    // Get the base price from the upsell card
    const originalPriceElement = upsellCard.querySelector('[data-upsell-original-price]');
    if (!originalPriceElement) {
      console.log('[Cart Upsell] No original price element found');
      return;
    }

    const originalPriceText = originalPriceElement.textContent.trim();
    const priceMatch = originalPriceText.match(/[\d,]+\.?\d*/);
    if (!priceMatch) {
      console.log('[Cart Upsell] Could not parse price from:', originalPriceText);
      return;
    }

    const originalPriceValue = parseFloat(priceMatch[0].replace(/,/g, ''));
    if (Number.isNaN(originalPriceValue)) {
      console.log('[Cart Upsell] Invalid price value');
      return;
    }

    // Convert to cents and calculate discounted price
    const originalPriceInCents = Math.round(originalPriceValue * 100);
    const discountedPriceInCents = Math.round(originalPriceInCents * discountMultiplier);
    const formattedDiscountedPrice = this.formatMoney(discountedPriceInCents);

    console.log('[Cart Upsell] Price calculation:', {
      originalPriceInCents,
      discountedPriceInCents,
      formattedDiscountedPrice
    });

    // Find the cart item that matches the upsell product
    // We'll look for the most recently added item or match by product ID if available
    const cartItems = document.querySelectorAll('.cart__item');
    console.log('[Cart Upsell] Found cart items:', cartItems.length);
    let foundItem = null;

    // First, try to find by data attributes or product ID
    cartItems.forEach((item, index) => {
      // Check if this item has the upsell product ID in any data attribute
      const itemKey = item.dataset.item;
      if (itemKey) {
        // The most recently added item is likely the upsell item
        foundItem = item;
        console.log('[Cart Upsell] Found potential upsell item at index:', index, itemKey);
      }
    });

    // If not found, get the first/last item (most recently added is usually last)
    if (!foundItem && cartItems.length > 0) {
      foundItem = cartItems[cartItems.length - 1];
      console.log('[Cart Upsell] Using last cart item as upsell item');
    }

    if (foundItem) {
      console.log('[Cart Upsell] Updating price display for cart item');
      const priceContainer = foundItem.querySelector('.cart__price');
      if (priceContainer) {
        // Find or create the ins element for the discounted price
        let insElement = priceContainer.querySelector('ins');
        if (!insElement) {
          // Create ins element if it doesn't exist
          insElement = document.createElement('ins');
          priceContainer.insertBefore(insElement, priceContainer.firstChild);
          console.log('[Cart Upsell] Created new <ins> element for discounted price');
        }
        insElement.textContent = formattedDiscountedPrice + ' ';
        console.log('[Cart Upsell] Updated discounted price to:', formattedDiscountedPrice);

        // Update or add the strikethrough original price
        let delElement = priceContainer.querySelector('del');
        if (!delElement) {
          delElement = document.createElement('del');
          priceContainer.insertBefore(delElement, insElement);
          console.log('[Cart Upsell] Created new <del> element for original price');
        }
        delElement.textContent = originalPriceText + ' ';
        console.log('[Cart Upsell] Updated original price to:', originalPriceText);

        // Update savings badge if it exists
        const savingsElement = priceContainer.querySelector('.price-difference');
        if (savingsElement) {
          const savings = originalPriceInCents - discountedPriceInCents;
          savingsElement.textContent = `Save ${this.formatMoney(savings)}`;
          console.log('[Cart Upsell] Updated savings badge');
        }
      } else {
        console.log('[Cart Upsell] No price container found in cart item');
      }
    } else {
      console.log('[Cart Upsell] No cart item found to update');
    }
  }

  cartHasDiscount(cartData, discountCode) {
    console.log('[Cart Upsell] cartHasDiscount called', { cartData: !!cartData, discountCode });
    if (!cartData || !discountCode) {
      console.log('[Cart Upsell] Missing cartData or discountCode');
      return false;
    }

    const normalizedCode = discountCode.toString().toLowerCase();
    console.log('[Cart Upsell] Checking for normalized discount code:', normalizedCode);

    if (Array.isArray(cartData.discount_codes)) {
      console.log('[Cart Upsell] Checking discount_codes array:', cartData.discount_codes);
      const hasCode = cartData.discount_codes.some((entry) => {
        const code = (entry?.code || '').toString().toLowerCase();
        return code === normalizedCode;
      });
      if (hasCode) {
        console.log('[Cart Upsell] Discount found in discount_codes');
        return true;
      }
    }

    if (Array.isArray(cartData.cart_level_discount_applications)) {
      console.log('[Cart Upsell] Checking cart_level_discount_applications array:', cartData.cart_level_discount_applications);
      const hasApplication = cartData.cart_level_discount_applications.some((application) => {
        const code = (application?.code || '').toString().toLowerCase();
        const title = (application?.title || '').toString().toLowerCase();
        return code === normalizedCode || title === normalizedCode;
      });
      if (hasApplication) {
        console.log('[Cart Upsell] Discount found in cart_level_discount_applications');
        return true;
      }
    }

    console.log('[Cart Upsell] Discount not found in cart');
    return false;
  }

  async applyDiscountCode(discountCode) {
    console.log('[Cart Upsell] applyDiscountCode called with:', discountCode);
    if (!discountCode) {
      console.log('[Cart Upsell] No discount code provided');
      return;
    }

    try {
      // First, check if there are existing discount codes
      console.log('[Cart Upsell] Checking for existing discount codes...');
      const currentCartData = await this.fetchCartData();
      const existingDiscountCodes = [];
      
      if (currentCartData) {
        // Get existing discount codes from discount_codes array
        if (Array.isArray(currentCartData.discount_codes)) {
          currentCartData.discount_codes.forEach(entry => {
            if (entry.code && entry.code.toLowerCase() !== discountCode.toLowerCase()) {
              existingDiscountCodes.push(entry.code);
            }
          });
        }
        
        // Also check cart_level_discount_applications
        if (Array.isArray(currentCartData.cart_level_discount_applications)) {
          currentCartData.cart_level_discount_applications.forEach(application => {
            if (application.code && application.code.toLowerCase() !== discountCode.toLowerCase()) {
              if (!existingDiscountCodes.includes(application.code)) {
                existingDiscountCodes.push(application.code);
              }
            }
          });
        }
      }
      
      console.log('[Cart Upsell] Existing discount codes found:', existingDiscountCodes);
      
      // If there are existing discount codes, use the /discount/ URL method
      // which preserves existing discounts better than /cart/update.js
      // We'll use a hidden iframe to apply the discount without redirecting
      if (existingDiscountCodes.length > 0) {
        console.log('[Cart Upsell] Existing discounts detected, using /discount/ URL method to preserve them...');
        const encoded = encodeURIComponent(discountCode);
        const discountUrl = `/discount/${encoded}`;
        console.log('[Cart Upsell] Using discount URL:', discountUrl);
        
        // Create a hidden iframe to apply the discount without redirecting
        return new Promise((resolve, reject) => {
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.style.width = '0';
          iframe.style.height = '0';
          iframe.src = discountUrl;
          
          iframe.onload = () => {
            console.log('[Cart Upsell] Discount iframe loaded');
            // Wait a bit for the discount to be applied server-side
            setTimeout(async () => {
              // Remove iframe
              iframe.remove();
              
              // Fetch updated cart data
              const updatedCartData = await this.fetchCartData();
              console.log('[Cart Upsell] Cart data after discount URL method:', updatedCartData);
              
              resolve(updatedCartData);
            }, 800);
          };
          
          iframe.onerror = (error) => {
            console.error('[Cart Upsell] Error loading discount iframe:', error);
            iframe.remove();
            reject(error);
          };
          
          document.body.appendChild(iframe);
        });
      } else {
        // No existing discounts, safe to use /cart/update.js
        console.log('[Cart Upsell] No existing discounts, using /cart/update.js...');
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            discount: discountCode
          }),
        });

        console.log('[Cart Upsell] Discount API response status:', response.status, response.statusText);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const cartData = await response.json();
        console.log('[Cart Upsell] Discount applied successfully, cart data:', cartData);
        
        return cartData;
      }
    } catch (error) {
      console.error('[Cart Upsell] Error applying discount via API, trying fallback method:', error);
      // Fallback to redirect method
      const encoded = encodeURIComponent(discountCode);
      const discountUrl = `/discount/${encoded}?redirect=/cart`;
      console.log('[Cart Upsell] Trying fallback discount URL:', discountUrl);
      await fetch(discountUrl, {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'follow',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
    }
  }

  handleVariantChange(select) {
    const selectedOption = select.options[select.selectedIndex];
    const upsellCard = select.closest('[data-cart-upsell]');
    const variantId = selectedOption.value;
    
    if (selectedOption && selectedOption.dataset.variantImage) {
      this.updateProductImage(upsellCard, selectedOption.dataset.variantImage);
    }
    if (selectedOption && selectedOption.dataset.variantPrice) {
      const variantPrice = parseInt(selectedOption.dataset.variantPrice, 10);
      if (!Number.isNaN(variantPrice)) {
        this.updatePricingDisplay(upsellCard, variantPrice);
      }
    }

    // Update the hidden input fields in all forms within this upsell card
    this.updateFormVariant(upsellCard, variantId);
  }

  updateFormVariant(upsellCard, variantId) {
    // Update all hidden id inputs in forms within this upsell card
    const idInputs = upsellCard.querySelectorAll('input[name="id"]');
    idInputs.forEach(input => {
      input.value = variantId;
    });

    // Also update any form attributes that reference the variant
    const forms = upsellCard.querySelectorAll('form[action*="/cart/add"]');
    forms.forEach(form => {
      const formIdInputs = form.querySelectorAll('input[name="id"]');
      formIdInputs.forEach(input => {
        input.value = variantId;
      });
    });
  }

  updateProductImage(upsellCard, imageUrl) {
    const imageElement = upsellCard.querySelector('[data-upsell-image]');
    if (imageElement) {
      // Add fade out effect
      imageElement.style.opacity = '0';
      
      setTimeout(() => {
        imageElement.src = imageUrl;
        imageElement.style.opacity = '1';
      }, 150);
    }
  }

  setFormLoadingState(form, isLoading) {
    const buttons = form.querySelectorAll('[data-add-to-cart]');
    
    buttons.forEach(button => {
      if (isLoading) {
        button.disabled = true;
        button.classList.add('loading');
        
        // Ensure the button has the proper structure for loading state
        if (!button.querySelector('.btn__loader')) {
          const loader = document.createElement('div');
          loader.className = 'btn__loader';
          loader.innerHTML = '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none"/></svg>';
          button.appendChild(loader);
        }
        
        // Ensure the button has the proper structure for text
        if (!button.querySelector('.btn__text')) {
          const text = document.createElement('span');
          text.className = 'btn__text';
          text.textContent = button.textContent;
          button.innerHTML = '';
          button.appendChild(text);
          
          // Re-add the loader
          const loader = document.createElement('div');
          loader.className = 'btn__loader';
          loader.innerHTML = '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none"/></svg>';
          button.appendChild(loader);
        }
      } else {
        button.disabled = false;
        button.classList.remove('loading');
      }
    });
  }

  /**
   * Fetch cart data from Shopify's cart API
   */
  async fetchCartData() {
    console.log('[Cart Upsell] fetchCartData called');
    try {
      console.log('[Cart Upsell] Fetching /cart.js...');
      const response = await fetch('/cart.js');
      console.log('[Cart Upsell] Cart API response status:', response.status);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const cartData = await response.json();
      console.log('[Cart Upsell] Cart data fetched:', cartData);
      
      // Validate that we have the expected cart data structure
      if (!cartData || typeof cartData.total_price === 'undefined') {
        console.log('[Cart Upsell] Invalid cart data, retrying...');
        // Retry once after a short delay
        await new Promise(resolve => setTimeout(resolve, 200));
        const retryResponse = await fetch('/cart.js');
        if (retryResponse.ok) {
          const retryData = await retryResponse.json();
          if (retryData && typeof retryData.total_price !== 'undefined') {
            console.log('[Cart Upsell] Retry successful, cart data:', retryData);
            return retryData;
          }
        }
        console.log('[Cart Upsell] Retry failed, returning null');
        return null;
      }
      
      return cartData;
    } catch (error) {
      console.error('[Cart Upsell] Error fetching cart data:', error);
      return null;
    }
  }

  /**
   * Fetch cart data with cache busting to ensure fresh data
   */
  async fetchCartDataFresh() {
    try {
      // Add timestamp to prevent caching
      const timestamp = Date.now();
      const response = await fetch(`/cart.js?t=${timestamp}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const cartData = await response.json();
      
      // Validate that we have the expected cart data structure
      if (!cartData || typeof cartData.total_price === 'undefined') {
        // Retry once after a short delay
        await new Promise(resolve => setTimeout(resolve, 200));
        const retryResponse = await fetch(`/cart.js?t=${Date.now()}`);
        if (retryResponse.ok) {
          const retryData = await retryResponse.json();
          if (retryData && typeof retryData.total_price !== 'undefined') {
            return retryData;
          }
        }
        return null;
      }
      
      return cartData;
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if the upsell product is already in the cart
   */
  isUpsellProductInCart(cartData) {
    if (!this.upsellProductId || !cartData || !cartData.items) {
      return false;
    }

    // Check if any cart item matches the upsell product ID
    const isInCart = cartData.items.some(item => {
      // Check both variant ID and product ID
      return item.variant_id.toString() === this.upsellProductId.toString() ||
             item.product_id.toString() === this.upsellProductId.toString();
    });
    
    return isInCart;
  }

  /**
   * Check if cart data is valid and ready
   */
  isCartDataValid(cartData) {
    return cartData && 
           typeof cartData.total_price !== 'undefined' && 
           Array.isArray(cartData.items) &&
           this.upsellProductId;
  }

  /**
   * Get cart value in cents
   */
  getCartValueInCents(cartData) {
    if (!cartData || !cartData.total_price) {
      return 0;
    }
    // Shopify returns total_price in cents
    return cartData.total_price;
  }

  getCartItemCount(cartData) {
    if (!cartData) {
      return 0;
    }

    if (typeof cartData.item_count === 'number') {
      return cartData.item_count;
    }

    if (Array.isArray(cartData.items)) {
      return cartData.items.reduce((total, item) => {
        const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
        return total + quantity;
      }, 0);
    }

    return 0;
  }

  /**
   * Check and update visibility for both cart upsell card and cart reviews
   */
  async checkVisibility() {
    // Prevent multiple simultaneous visibility checks
    if (this.isCheckingVisibility) {
      return;
    }

    this.isCheckingVisibility = true;
    
    try {
      const cartData = await this.fetchCartData();
      if (!this.isCartDataValid(cartData)) {
        // If cart data isn't ready, schedule another check
        setTimeout(() => this.debouncedCheckVisibility(), 1000);
        return;
      }

      const cartValueInCents = this.getCartValueInCents(cartData);
      const cartItemCount = this.getCartItemCount(cartData);
      const isUpsellInCart = this.isUpsellProductInCart(cartData);

      // Determine cart upsell card visibility
      const hasMinimumItems = cartItemCount >= this.minimumItemsForUpsell;
      const shouldShowUpsell = hasMinimumItems && !isUpsellInCart;
      
      // Update cart upsell card visibility
      this.updateUpsellCardVisibility(shouldShowUpsell);
      
      // Update cart reviews visibility (only show if upsell is not visible)
      this.updateCartReviewsVisibility(!shouldShowUpsell);
    } catch (error) {
      // Error checking visibility
    } finally {
      this.isCheckingVisibility = false;
    }
  }

  /**
   * Check visibility with retry mechanism to ensure we get updated cart data
   */
  async checkVisibilityWithRetry(maxRetries = 3, initialDelay = 800) {
    let retryCount = 0;
    let delay = initialDelay;

    const attemptCheck = async () => {
      try {
        // Use fresh cart data to prevent caching issues
        const cartData = await this.fetchCartDataFresh();
        if (!this.isCartDataValid(cartData)) {
          throw new Error('Cart data not valid');
        }

        const cartValueInCents = this.getCartValueInCents(cartData);
        const cartItemCount = this.getCartItemCount(cartData);
        const isUpsellInCart = this.isUpsellProductInCart(cartData);

        // Determine cart upsell card visibility
        const hasMinimumItems = cartItemCount >= this.minimumItemsForUpsell;
        const shouldShowUpsell = hasMinimumItems && !isUpsellInCart;
        
        // Update cart upsell card visibility
        this.updateUpsellCardVisibility(shouldShowUpsell);
        
        // Update cart reviews visibility (only show if upsell is not visible)
        this.updateCartReviewsVisibility(!shouldShowUpsell);

        return true; // Success
      } catch (error) {
        retryCount++;
        
        if (retryCount < maxRetries) {
          // Exponential backoff
          delay = delay * 1.5;
          setTimeout(() => attemptCheck(), delay);
        } else {
          this.checkVisibility(); // Fallback to regular check
        }
        return false;
      }
    };

    // Start the retry process
    setTimeout(() => attemptCheck(), initialDelay);
  }

  /**
   * Update cart upsell card visibility
   */
  updateUpsellCardVisibility(shouldShow) {
    // Only update visibility if cart is not currently updating
    if (this.isCartUpdating) {
      return;
    }

    const upsellCards = document.querySelectorAll('[data-cart-upsell]');
    
    upsellCards.forEach(card => {
      if (shouldShow) {
        card.classList.remove('hidden');
        // Store the visibility state in a data attribute for persistence
        card.dataset.shouldBeVisible = 'true';
      } else {
        card.classList.add('hidden');
        // Store the visibility state in a data attribute for persistence
        card.dataset.shouldBeVisible = 'false';
      }
    });
  }

  /**
   * Update cart reviews visibility
   */
  updateCartReviewsVisibility(shouldShow) {
    // Only update visibility if cart is not currently updating
    if (this.isCartUpdating) {
      return;
    }

    const cartReviews = document.querySelectorAll('.CP_TE_Sitewide_07_V2');
    
    cartReviews.forEach(review => {
      if (shouldShow) {
        review.classList.remove('hidden');
      } else {
        review.classList.add('hidden');
      }
    });
  }

  /**
   * Refresh cart data and update visibility
   */
  refreshCart() {
    console.log('[Cart Upsell] refreshCart called');
    console.log('[Cart Upsell] Step 1: Dispatching cart:refresh event...');
    // Dispatch custom event to refresh cart
    const refreshEvent = new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { source: 'visibility-manager' }
    });
    document.dispatchEvent(refreshEvent);
    console.log('[Cart Upsell] cart:refresh event dispatched');
    
    // Also try to trigger cart drawer refresh if it exists
    // The cart is stored as window.cart (not window.theme.cart)
    if (window.cart && typeof window.cart.getCart === 'function') {
      try {
        console.log('[Cart Upsell] Step 2: Calling window.cart.getCart()...');
        window.cart.getCart();
        console.log('[Cart Upsell] window.cart.getCart() called successfully');
      } catch (e) {
        console.log('[Cart Upsell] window.cart.getCart() error:', e);
      }
    } else {
      console.log('[Cart Upsell] window.cart not available or getCart method missing', {
        cartExists: !!window.cart,
        hasGetCart: window.cart && typeof window.cart.getCart === 'function'
      });
    }

    // Try to trigger cart refresh on the cart drawer if it exists
    const cartDrawer = document.querySelector('[data-cart-drawer]');
    if (cartDrawer && cartDrawer.refreshCart) {
      try {
        console.log('[Cart Upsell] Step 3: Calling cartDrawer.refreshCart()...');
        cartDrawer.refreshCart();
        console.log('[Cart Upsell] cartDrawer.refreshCart() called successfully');
      } catch (e) {
        console.log('[Cart Upsell] cartDrawer.refreshCart() not available:', e);
      }
    } else {
      console.log('[Cart Upsell] cartDrawer element or refreshCart method not found');
    }

    // Try to trigger cart refresh on the cart page if it exists
    const cartPage = document.querySelector('[data-cart-page]');
    if (cartPage && cartPage.refreshCart) {
      try {
        console.log('[Cart Upsell] Step 4: Calling cartPage.refreshCart()...');
        cartPage.refreshCart();
        console.log('[Cart Upsell] cartPage.refreshCart() called successfully');
      } catch (e) {
        console.log('[Cart Upsell] cartPage.refreshCart() not available:', e);
      }
    } else {
      console.log('[Cart Upsell] cartPage element or refreshCart method not found');
    }

    // Schedule a visibility check after the refresh completes
    console.log('[Cart Upsell] Step 5: Scheduling visibility check in 500ms...');
    setTimeout(() => {
      console.log('[Cart Upsell] Running scheduled visibility check...');
      this.debouncedCheckVisibility();
    }, 500);
  }

  /**
   * Manually check visibility (can be called from other scripts)
   */
  manualCheck() {
    this.checkVisibility();
  }

  /**
   * Cleanup observers and event listeners
   */
  cleanup() {
    if (this.observers) {
      this.observers.forEach(observer => observer.disconnect());
      this.observers = [];
    }
    if (this.visibilityCheckTimeout) {
      clearTimeout(this.visibilityCheckTimeout);
      this.visibilityCheckTimeout = null;
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.cartVisibilityManager = new CartVisibilityManager();
});

// Also initialize for dynamically loaded content (like cart drawer)
document.addEventListener('cart:loaded', () => {
  window.cartVisibilityManager = new CartVisibilityManager();
});

// Export for potential use in other scripts
window.CartVisibilityManager = CartVisibilityManager;

// Also export a global function to manually check visibility
window.checkCartVisibility = () => {
  if (window.cartVisibilityManager) {
    window.cartVisibilityManager.manualCheck();
  }
};

// Export function to restore visibility state after re-rendering
window.restoreCartVisibilityState = () => {
  if (window.cartVisibilityManager) {
    window.cartVisibilityManager.restoreVisibilityState();
  }
};

// Export function to force visibility check with retry
window.checkCartVisibilityWithRetry = () => {
  if (window.cartVisibilityManager) {
    window.cartVisibilityManager.checkVisibilityWithRetry(3, 800);
  }
};

// Export function to manually check visibility for debugging
window.debugCartVisibility = () => {
  if (window.cartVisibilityManager) {
    window.cartVisibilityManager.checkVisibility();
  }
};

// Export function to show current state
window.showCartVisibilityState = () => {
  if (window.cartVisibilityManager) {
    // Return state object for inspection
    return {
      isCartUpdating: window.cartVisibilityManager.isCartUpdating,
      isCheckingVisibility: window.cartVisibilityManager.isCheckingVisibility,
      upsellProductId: window.cartVisibilityManager.upsellProductId,
      minimumItemsForUpsell: window.cartVisibilityManager.minimumItemsForUpsell
    };
  }
};

// Export function to manually reset cart updating state
window.resetCartUpdatingState = () => {
  if (window.cartVisibilityManager) {
    window.cartVisibilityManager.setCartUpdating(false);
    // Force a visibility check
    window.cartVisibilityManager.checkVisibility();
  }
};

// Export function to manually hide promotional item quantity selectors and show remove buttons
window.hidePromotionalQuantitySelectors = () => {
  if (window.cartVisibilityManager) {
    window.cartVisibilityManager.hidePromotionalItemQuantitySelectors();
  }
};

// Export function to apply promotional item styles
window.applyPromotionalItemStyles = () => {
  if (window.cartVisibilityManager) {
    window.cartVisibilityManager.applyPromotionalItemStyles();
  }
};
